import React, { useCallback, useEffect, useState } from 'react';
import { DocumentsClientService, AnalyticsService } from '$/services';
import * as Bixby from '$/core';
import { CLIENT_COMPANY_DOCUMENTS } from '$/core/constants';
import NotFound from '$/pages/not-found';
import Loading from '$/components/loading/Loading';
import classnames from 'classnames';
import './index.scss';
import { PartialCompany } from '$/components/client-company-documents-list/models';

interface ICompanyDocumentsProps {
  currentCompany?: PartialCompany;
  featureFlags?: string[];
  isLoading?: boolean;
  match?: any;
  profile?: Bixby.Models.Profile;
  dispatchDownloadDocument?: (blob: Blob, fileName: string) => Promise<void>;
  dispatchDownloadFailure?: () => Promise<void>;
}

const ClientCompanyDocumentsDownload: React.FC<ICompanyDocumentsProps> = props => {
  const { profile, featureFlags, dispatchDownloadDocument, dispatchDownloadFailure, currentCompany, match } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(false);

  const downloadDocument = useCallback(async () => {
    try {
      if (isLoading || isInitialLoading) return;
      const { documentId } = match.params;
      const { blob, filename } = await DocumentsClientService.downloadDocument(documentId);

      if (profile) {
        AnalyticsService.event(Bixby.Constants.APPINSIGHTS_DOCUMENTS_DOWNLOAD, {
          ...profile,
          companyId: currentCompany?.id,
          companyName: currentCompany?.name,
          documentId,
        });
      }

      if (dispatchDownloadDocument) {
        dispatchDownloadDocument(blob, (filename || '').replace(/['"]/g, ''));
      }
    } catch (err: any) {
      if (dispatchDownloadFailure) {
        dispatchDownloadFailure();
      }
    } finally {
      setIsLoading(false);
      setIsInitialLoading(false);
    }
  }, [
    dispatchDownloadDocument,
    dispatchDownloadFailure,
    isLoading,
    isInitialLoading,
    match.params,
    profile,
    currentCompany,
  ]);

  useEffect(() => {
    if (featureFlags && featureFlags.includes(CLIENT_COMPANY_DOCUMENTS) && currentCompany) {
      setIsInitialLoading(true);
      downloadDocument();
    }
  }, [featureFlags, currentCompany, downloadDocument]);

  if (
    !(featureFlags || []).includes(CLIENT_COMPANY_DOCUMENTS) ||
    !currentCompany ||
    Bixby.Utils.isNullOrEmpty(currentCompany)
  ) {
    return <NotFound />;
  } else if (isInitialLoading || props.isLoading) {
    return <Loading pageLevel />;
  } else {
    return (
      <section className="documents_contain">
        <div className="download_contain">
          <p>
            The file will download shortly. To manually download the file,{' '}
            <span
              className={classnames('fake_link', { disabled: isInitialLoading })}
              role="button"
              onClick={() => {
                setIsLoading(true);
                downloadDocument();
              }}>
              click here
            </span>
            .
          </p>
          {isLoading && <Loading inline />}
        </div>
      </section>
    );
  }
};

export default ClientCompanyDocumentsDownload;
